import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollectionLayer } from '@adornis/baseql/server/collections.js';
import { assertIsClientContext, context } from '@adornis/baseql/server/context.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { assertCanManageUsers } from '../server/access.js';

/**
 * Continue this session as another account. The administrator stays on the context as
 * `impersonatingUser` — that is what endImpersonation hands the session back to and what
 * the client reads for the banner.
 *
 * Own operation instead of the engine's `impersonateUser`: that one asserts
 * UserPermission.Impersonate, and the Admin role here carries a name only (db/roles.ts),
 * so hasPermission() never matches. Access is decided in canManageUsers like everywhere else.
 *
 * @returns a display name for the account taken over
 */
export const startImpersonation = registerMutation({
  type: () => String,
  operationName: 'startImpersonation',
  resolve: (userID: string) => {
    return async () => {
      assertIsClientContext(context);
      // Before the permission check: while impersonating, the caller *is* the taken-over
      // account, and a second hop would overwrite the way back to the administrator.
      if (context.impersonatingUser) throw new ExpectedError('Beende zuerst die laufende Übernahme.');

      const callerID = await assertCanManageUsers();
      if (userID === callerID) throw new ExpectedError('Du bist bereits mit diesem Konto angemeldet.');

      const users = await getCollectionLayer<AdornisUser>(AdornisUser._collectionName);
      const target = await users.findOne(
        { _id: userID },
        { projection: { _id: 1, _class: 1, username: 1, email: 1, archivedAt: 1 } },
      );
      if (!target) throw new ExpectedError('Dieses Konto existiert nicht (mehr).');
      // The publishFilter hides deactivated accounts, so the currentUser subscription would
      // run empty and the client would log the whole session out (see client/startup.ts).
      if (target.archivedAt) throw new ExpectedError('Ein deaktiviertes Konto kann nicht übernommen werden.');

      context.impersonatingUser = context.userID;
      context.impersonatingUserClass = context.userClass;
      // Recreates the context, so entityPermissions and publishFilter are rebuilt for the
      // taken-over account, and writes the session to the store.
      await setUserOnContext({ _id: target._id, _class: target._class });

      logger.info({ userID: callerID, impersonatedUserID: target._id }, 'Impersonation gestartet');
      return target.username || target.email || target._id;
    };
  },
  params: [{ name: 'userID', type: () => String }],
});
